import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { parse, parseISO, differenceInSeconds, addDays, addHours, getDay, fromUnixTime } from 'date-fns'
import { da, de, enGB, es, fi, fr, it, nl, pt, sv } from 'date-fns/locale';

import { toZonedTime, format, getTimezoneOffset } from 'date-fns-tz'

@Injectable({
  providedIn: 'root'
})
export class DateUtilsService {

  constructor(
    private translate: TranslateService,) { 
  }

  getLocale(code: String) {
    if(code == 'da')
      return da;
    if(code == 'de')
      return de;
    if(code == 'en')
      return enGB;
    if(code == 'es')
      return es;
    if(code == 'fi')
      return fi;
    if(code == 'fr')
      return fr;
    if(code == 'it')
      return it;
    if(code == 'nl')
      return nl;
    if(code == 'pt')
      return pt;
    if(code == 'sv')
      return sv;
    return enGB;
  }

  toString(date: Date | null, locale = fr) {
    if(date == null)
      return '';

    return format(date, 'dd/MM/yyyy HH:mm:ss', {locale: this.getLocale(this.translate.currentLang),});
  }

  toStringMonthYear(date: Date, locale = fr) {
    if(date == null)
      return '';

    return format(date, 'MM/yyyy', {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringYear(date: Date, locale = fr) {
    if(date == null)
      return '';

    return format(date, 'yyyy', {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringWithoutYear(date: Date, locale = fr) {
    if(date == null)
      return '';

    return format(date, 'dd/MM HH:mm:ss', {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringWithoutYearWithoutSeconds(date: Date | null, locale = fr) {
    if(date == null)
      return '';

    return format(date, 'dd/MM HH:mm', {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringWithoutSeconds(date: Date | null, locale = fr) {
    if(date == null)
      return '';

    return format(date, 'dd/MM/yyyy HH:mm', {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringWithoutTime(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, 'dd/MM/yyyy', {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringIso(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, "yyyy-MM-dd'T'HH:mm:ssXXXXX", {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringIsoWithoutTimezone(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, "yyyy-MM-dd'T'HH:mm:ss", {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringIsoWithoutTime(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, "yyyy-MM-dd", {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringHour(date: Date, locale = fr) {
    if(date == null)
      return '';
    return format(date, "HH:mm:ss", {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringWithoutTimeWithDay(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, 'EEEE d LLLL yyyy', {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringWithoutTimeWithShortDay(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, 'd LLLL yyyy', {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringShortWithoutTimeWithDay(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, 'd LLL yyyy', {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringShortWithTimeWithDay(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, 'd LLL yyyy HH:mm', {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringWithTimeWithoutYear(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, 'EEEE d LLLL HH:mm:ss', {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringWithTimeWithoutYearSeconds(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, 'EEEE d LLLL HH:mm', {locale: this.getLocale(this.translate.currentLang)});
  }


  toStringWithDay(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, 'EEEE d LLLL yyyy', {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringWithDayWithoutYear(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, 'EEEE d LLLL', {locale: this.getLocale(this.translate.currentLang)});
  }

  toStringWithDayMonth(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, 'd LLLL', {locale: this.getLocale(this.translate.currentLang)});
  }

  fromString(date: string | null): Date | null {
    if(date == null)
      return null;

    return date ? toZonedTime(parseISO(date), Intl.DateTimeFormat().resolvedOptions().timeZone): null;
  }
  
  fromTimestamp(date: number) {
    return date ? toZonedTime(fromUnixTime(date), Intl.DateTimeFormat().resolvedOptions().timeZone): null;
  }

  monthYearLabel(date: Date, locale = fr) {
    var ret: string | null = format(date, 'LLLL yyyy', {locale: this.getLocale(this.translate.currentLang)})
    if(ret) {
      ret = ret.charAt(0).toUpperCase() + ret.slice(1)
    }
    return ret;
  }

  differenceInSeconds(date1: string, date2: string) {
    return Math.abs(differenceInSeconds(parseISO(date1), parseISO(date2)))
  }

  datefusion_full( date1: Date | null, date2: Date | null, locale = fr ) {
    var ret: string = '';
    if (date1 == null && date2 == null)
      ret = ''
    else if(date1 != null && date2 == null)
      ret = format(date1, 'EEEE d LLLL yyyy HH:mm', {locale: this.getLocale(this.translate.currentLang)})
    else if(date2 != null && date1 == null)
      ret = format(date2, 'EEEE d LLLL yyyy HH:mm', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null && date1.getDay() == date2.getDay() && date1.getMonth() == date2.getMonth() && date1.getFullYear() == date2.getFullYear() && date1.getHours() == date2.getHours() && date1.getMinutes() == date2.getMinutes() && date1.getSeconds() == date2.getSeconds())
      ret = format(date1, 'EEEE d LLLL yyyy HH:mm', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null && date1.getDay() == date2.getDay() && date1.getMonth() == date2.getMonth() && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, 'EEEE d LLLL yyyy HH:mm', {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, 'HH:mm', {locale: this.getLocale(this.translate.currentLang)})
      
    else if(date1 != null && date2 != null && date1.getMonth() == date2.getMonth() && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, 'EEEE d LLLL yyyy HH:mm', {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, 'EEEE d HH:mm', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, 'EEEE d LLLL yyyy HH:mm LLLL', {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, 'EEEE d HH:mm', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null)
      ret = format(date1, 'EEEE d LLLL yyyy HH:mm LLLL yyyy', {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, 'EEEE d HH:mm', {locale: this.getLocale(this.translate.currentLang)})
    return this.capitalizeFirstLetter(ret)
  }

  capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  
  datefusion_full_multilineslinesHTML( date1: Date | null, date2: Date | null, locale = fr ) {
    var ret: string = '';
    if (date1 == null && date2 == null)
      ret = ''
    else if(date1 != null && date2 == null)
      ret = format(date1, "EEEE'\n'd/MM/yyyy'\n'HH:mm", {locale: this.getLocale(this.translate.currentLang)})
    else if(date2 != null && date1 == null)
      ret = format(date2, "EEEE'\n'd/MM/yyyy'\n'HH:mm", {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null && date1.getDay() == date2.getDay() && date1.getMonth() == date2.getMonth() && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, "EEEE'\n'd/MM/yyyy'\n'HH:mm", {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, "HH:mm", {locale: this.getLocale(this.translate.currentLang)})
      
    else if(date1 != null && date2 != null && date1.getMonth() == date2.getMonth() && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, "EEEE'\n'd/MM/yyyy'\n'HH:mm", {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, '\nEEEE d HH:mm', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, "EEEE'\n'd/MM/yyyy'\n'HH:mm LLLL", {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, '\nEEEE d\nHH:mm', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null)
      ret = format(date1, "EEEE'\n'd/MM/yyyy'\n'HH:mm LLLL yyyy", {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, '\nEEEE d\nHH:mm', {locale: this.getLocale(this.translate.currentLang)})
    return this.capitalizeFirstLetter(ret)
  }
  
  datefusion_withoutYear( date1: Date | null, date2: Date | null, locale = fr ) {
    var ret: string = '';
    if (date1 == null && date2 == null)
      ret = ''
    else if(date1 != null && date2 == null)
      ret = format(date1, 'EEEE d LLLL', {locale: this.getLocale(this.translate.currentLang)})
    else if(date2 != null && date1 == null)
      ret = format(date2, 'EEEE d LLLL', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null && date1.getDay() == date2.getDay() && date1.getMonth() == date2.getMonth() && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, 'EEEE d LLLL', {locale: this.getLocale(this.translate.currentLang)})
      
    else if(date1 != null && date2 != null && date1.getMonth() == date2.getMonth() && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, 'EEEE d', {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, 'EEEE d LLLL', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null)
      ret = format(date1, 'EEEE d', {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, 'EEEE d LLLL', {locale: this.getLocale(this.translate.currentLang)})
    return ret
  }
  datefusion_shortMonth( date1: Date | null, date2: Date | null, locale = fr ) {
    var ret: string = '';
    if (date1 == null && date2 == null)
      ret = ''
    else if(date1 != null && date2 == null)
      ret = format(date1, 'dd/MM/yyyy', {locale: this.getLocale(this.translate.currentLang)})
    else if(date2 != null && date1 == null)
      ret = format(date2, 'dd/MM/yyyy', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null && date1.getDay() == date2.getDay() && date1.getMonth() == date2.getMonth() && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, 'dd/MM/yyyy', {locale: this.getLocale(this.translate.currentLang)})
      
    else if(date1 != null && date2 != null && date1.getMonth() == date2.getMonth() && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, 'dd/MM/yyyy', {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, 'd/MM/yyyy', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null)
      ret = format(date1, 'dd/MM/yyyy', {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, 'd/MM/yyyy', {locale: this.getLocale(this.translate.currentLang)})
    return ret
  }
  datefusion_withoutYear_shortMonth( date1: Date | null, date2: Date | null, locale = fr ) {
    var ret: string = '';
    if (date1 == null && date2 == null)
      ret = ''
    else if(date1 != null && date2 == null)
      ret = format(date1, 'EEEE d/MM', {locale: this.getLocale(this.translate.currentLang)})
    else if(date2 != null && date1 == null)
      ret = format(date2, 'EEEE d/MM', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null && date1.getDay() == date2.getDay() && date1.getMonth() == date2.getMonth() && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, 'EEEE d/MM', {locale: this.getLocale(this.translate.currentLang)})
      
    else if(date1 != null && date2 != null && date1.getMonth() == date2.getMonth() && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, 'EEEE d', {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, 'EEEE d/MM', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null)
      ret = format(date1, 'EEEE d', {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, 'EEEE d/MM', {locale: this.getLocale(this.translate.currentLang)})
    return ret
  }
  datefusion_withoutYear_short( date1: Date | null, date2: Date | null, locale = fr ) {
    var ret: string = '';
    if (date1 == null && date2 == null)
      ret = ''
    else if(date1 != null && date2 == null)
      ret = format(date1, 'EEEE d LLLL', {locale: this.getLocale(this.translate.currentLang)})
    else if(date2 != null && date1 == null)
      ret = format(date2, 'EEEE d LLLL', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null && date1.getDay() == date2.getDay() && date1.getMonth() == date2.getMonth() && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, 'EEEE d LLLL', {locale: this.getLocale(this.translate.currentLang)})
      
    else if(date1 != null && date2 != null && date1.getMonth() == date2.getMonth() && date1.getFullYear() == date2.getFullYear())
      ret = format(date1, 'EEEE d', {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, 'd LLLL', {locale: this.getLocale(this.translate.currentLang)})
    else if(date1 != null && date2 != null)
      ret = format(date1, 'EEEE d', {locale: this.getLocale(this.translate.currentLang)}) + ' - ' + format(date2, 'd LLLL', {locale: this.getLocale(this.translate.currentLang)})
    return ret
  }

  toStringHourWithoutSeconds(date: Date | null, locale = fr) {
    if(date == null)
      return '';
    return format(date, "HH:mm", {locale: this.getLocale(this.translate.currentLang)});
  }

}
